import { useCallback, useEffect, useMemo, useState } from "react";
import {
  obtenerRedesSocialesSetup,
  actualizarRedesSocialesSetup,
} from "../../../services/usuarios_tienda/tiendaSetup";
import "./ContactoTienda.css";

const USO_OPCIONES = [
  { value: "INFORMATIVO", label: "Informativo" },
  { value: "SERVICIO", label: "Servicio" },
];

const MOSTRAR_OPCIONES = [
  { value: "SIGUENOS", label: "Solo en la sección de Síguenos" },
  { value: "CONTACTO", label: "Solo en la sección de Contacto" },
  { value: "AMBOS", label: "Ambas secciones" },
];

function canalVacioDesdeCatalogo(cat) {
  const nombre =
    String(cat?.Nombre_visible || cat?.Nombre || cat?.Codigo || "").trim() || "Canal";

  return {
    tienda_canal_id: null,
    canales_contacto_id: Number(cat.canales_contacto_id),
    codigo: String(cat.Codigo || "").trim(),
    catalogo_nombre: cat.Nombre,
    catalogo_nombre_visible: cat.Nombre_visible,
    nombre_mostrar: nombre,
    valor: "",
    url: "",
    uso_canal: "INFORMATIVO",
    mostrar_en: "AMBOS",
    icono: "",
    es_principal: 0,
    orden: 0,
    estado: 1,
  };
}

function TabContactoTienda() {
  const [catalogo, setCatalogo] = useState([]);
  const [canales, setCanales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [addCanalId, setAddCanalId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);

    const res = await obtenerRedesSocialesSetup();

    if (res.ok && res.data) {
      setCatalogo(Array.isArray(res.data.catalogo) ? res.data.catalogo : []);
      setCanales(Array.isArray(res.data.canales) ? res.data.canales : []);
    } else {
      setMsg({
        type: "err",
        text: res.message || "No se pudieron cargar los canales.",
      });
      setCatalogo([]);
      setCanales([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const usadosIds = useMemo(
    () =>
      new Set(
        canales
          .filter((c) => Number(c.estado) === 1)
          .map((c) => Number(c.canales_contacto_id))
      ),
    [canales]
  );

  const catalogoDisponibleParaAgregar = useMemo(
    () => catalogo.filter((c) => !usadosIds.has(Number(c.canales_contacto_id))),
    [catalogo, usadosIds]
  );

  const patchCanal = (idx, field, value) => {
    setCanales((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const agregarCanal = () => {
    const id = Number(addCanalId);
    if (!id) return;

    const cat = catalogo.find((c) => Number(c.canales_contacto_id) === id);
    if (!cat) return;

    setCanales((prev) => {
      const idx = prev.findIndex(
        (c) => Number(c.canales_contacto_id) === id
      );
      if (idx !== -1) {
        const next = [...prev];
        const row = next[idx];
        next[idx] = {
          ...row,
          estado: 1,
          nombre_mostrar:
            String(row.nombre_mostrar || "").trim() ||
            canalVacioDesdeCatalogo(cat).nombre_mostrar,
        };
        return next;
      }
      return [...prev, canalVacioDesdeCatalogo(cat)];
    });
    setAddCanalId("");
    setMsg(null);
  };

  const eliminarCanal = (idx) => {
    const row = canales[idx];
    if (!row) return;

    const idNum = Number(row.tienda_canal_id);
    if (Number.isInteger(idNum) && idNum > 0) {
      patchCanal(idx, "estado", 0);
    } else {
      setCanales((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const guardar = async () => {
    const activos = canales.filter((c) => Number(c.estado) === 1);

    const payload = activos.map((c) => {
      const rawId = c.tienda_canal_id;
      const tid =
        rawId != null && String(rawId).trim() !== ""
          ? Number(rawId)
          : null;
      const item = {
        canales_contacto_id: Number(c.canales_contacto_id),
        nombre_mostrar: String(c.nombre_mostrar || "").trim(),
        valor: String(c.valor || "").trim(),
        url: String(c.url || "").trim(),
        uso_canal: String(c.uso_canal || "INFORMATIVO").toUpperCase(),
        mostrar_en: String(c.mostrar_en || "AMBOS").toUpperCase(),
        icono: String(c.icono || "").trim(),
        es_principal: Number(c.es_principal) ? 1 : 0,
        orden: Number.isFinite(Number(c.orden)) ? Number(c.orden) : 0,
        estado: 1,
      };
      if (Number.isInteger(tid) && tid > 0) {
        item.tienda_canal_id = tid;
      }
      return item;
    });

    const inactivos = canales.filter((c) => {
      const id = Number(c.tienda_canal_id);
      return Number.isInteger(id) && id > 0 && Number(c.estado) === 0;
    });

    const payloadInactivos = inactivos.map((c) => ({
      tienda_canal_id: Number(c.tienda_canal_id),
      canales_contacto_id: Number(c.canales_contacto_id),
      nombre_mostrar: String(c.nombre_mostrar || "").trim(),
      valor: String(c.valor || "").trim(),
      url: String(c.url || "").trim(),
      uso_canal: String(c.uso_canal || "INFORMATIVO").toUpperCase(),
      mostrar_en: String(c.mostrar_en || "AMBOS").toUpperCase(),
      icono: String(c.icono || "").trim(),
      es_principal: Number(c.es_principal) ? 1 : 0,
      orden: Number.isFinite(Number(c.orden)) ? Number(c.orden) : 0,
      estado: 0,
    }));

    if (!payload.length && !payloadInactivos.length) {
      setMsg({ type: "err", text: "No hay cambios para guardar." });
      return;
    }

    setSaving(true);
    setMsg(null);

    const res = await actualizarRedesSocialesSetup({
      canales: [...payload, ...payloadInactivos],
    });

    if (res.ok) {
      setMsg({ type: "ok", text: res.message || "Canales guardados." });
      await load();
    } else {
      setMsg({ type: "err", text: res.message });
    }

    setSaving(false);
  };

  if (loading) {
    return <div className="sky-contacto-state">Cargando canales...</div>;
  }

  if (!catalogo.length) {
    return (
      <div className="sky-contacto-empty">
        No hay catálogo de canales disponible.
      </div>
    );
  }

  const visibles = canales
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => Number(row.estado) === 1);

  return (
    <section className="sky-contacto-wrap">
      <div className="sky-contacto-hero">
        <div className="sky-contacto-hero__left">
          <span className="sky-contacto-hero__tag">Canales</span>
          <h2 className="sky-contacto-hero__title">Configuración de contacto</h2>
          <p className="sky-contacto-hero__text">
            Administra los medios de contacto que estarán visibles para tus clientes
            y define cómo se mostrarán dentro de tu tienda.
          </p>
        </div>

        <button
          type="button"
          className="sky-contacto-btn sky-contacto-btn--primary"
          disabled={saving}
          onClick={guardar}
        >
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>

      {msg && (
        <div
          className={`sky-contacto-alert ${
            msg.type === "ok"
              ? "sky-contacto-alert--ok"
              : "sky-contacto-alert--err"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="sky-contacto-add">
        <div className="sky-contacto-add__header">
          <div>
            <h3 className="sky-contacto-section-title">Agregar nuevo canal</h3>
            <p className="sky-contacto-section-text">
              Selecciona un tipo y añádelo a tu configuración.
            </p>
          </div>
        </div>

        <div className="sky-contacto-add__content">
          <div className="sky-contacto-field">
            <label>Canal disponible</label>
            <select
              value={addCanalId}
              onChange={(e) => setAddCanalId(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {catalogoDisponibleParaAgregar.map((c) => (
                <option key={c.canales_contacto_id} value={c.canales_contacto_id}>
                  {c.Nombre_visible }
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="sky-contacto-btn sky-contacto-btn--soft"
            disabled={!addCanalId}
            onClick={agregarCanal}
          >
            Añadir canal
          </button>
        </div>

        {catalogoDisponibleParaAgregar.length === 0 && (
          <p className="sky-contacto-note">
            Ya configuraste todos los tipos de canal disponibles.
          </p>
        )}
      </div>

      {visibles.length === 0 ? (
        <div className="sky-contacto-empty-card">
          No hay canales activos todavía.
        </div>
      ) : (
        <div className="sky-contacto-grid">
          {visibles.map(({ row, idx }) => (
            <article
              key={row.tienda_canal_id || `${row.canales_contacto_id}-${idx}`}
              className="sky-contacto-card"
            >
              <div className="sky-contacto-card__header">
                <div>
                  <span className="sky-contacto-card__code">
                    {String(row.codigo || row.Codigo || "Canal").toUpperCase()}
                  </span>
                  <h3 className="sky-contacto-card__title">
                    {row.catalogo_nombre_visible || row.catalogo_nombre || "Canal"}
                  </h3>
                </div>

                <button
                  type="button"
                  className="sky-contacto-btn sky-contacto-btn--danger"
                  onClick={() => eliminarCanal(idx)}
                >
                  Quitar
                </button>
              </div>

              <div className="sky-contacto-card__body">
                <div className="sky-contacto-field">
                  <label>Nombre a mostrar</label>
                  <input
                    value={row.nombre_mostrar || ""}
                    onChange={(e) =>
                      patchCanal(idx, "nombre_mostrar", e.target.value)
                    }
                    placeholder="Ej. WhatsApp ventas"
                  />
                </div>

                <div className="sky-contacto-field">
                  <label>Valor</label>
                  <input
                    value={row.valor || ""}
                    onChange={(e) => patchCanal(idx, "valor", e.target.value)}
                    placeholder="Teléfono, correo o usuario"
                  />
                </div>

                <div className="sky-contacto-field sky-contacto-field--full">
                  <label>URL</label>
                  <input
                    value={row.url || ""}
                    onChange={(e) => patchCanal(idx, "url", e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="sky-contacto-field">
                  <label>Uso del canal</label>
                  <select
                    value={row.uso_canal || "INFORMATIVO"}
                    onChange={(e) => patchCanal(idx, "uso_canal", e.target.value)}
                  >
                    {USO_OPCIONES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sky-contacto-field">
                  <label>Mostrar en</label>
                  <select
                    value={row.mostrar_en || "AMBOS"}
                    onChange={(e) => patchCanal(idx, "mostrar_en", e.target.value)}
                  >
                    {MOSTRAR_OPCIONES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sky-contacto-field">
                  <label>Orden</label>
                  <input
                    type="number"
                    value={row.orden ?? 0}
                    onChange={(e) =>
                      patchCanal(idx, "orden", Number(e.target.value))
                    }
                  />
                </div>

                <div className="sky-contacto-field sky-contacto-field--checkbox">
                  <label className="sky-contacto-check">
                    <input
                      type="checkbox"
                      checked={!!Number(row.es_principal)}
                      onChange={(e) =>
                        patchCanal(idx, "es_principal", e.target.checked ? 1 : 0)
                      }
                    />
                    <span>Canal principal</span>
                  </label>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default TabContactoTienda;